import {
    ApplicationRef,
    ComponentFactoryResolver,
    ComponentRef,
    EmbeddedViewRef,
    Injectable,
    Injector,
    Type,
} from '@angular/core';
import { DrawerComponent } from './drawer.component';
import { DrawerConfig, DrawerConfigDefaults } from './drawer-config';
import { DrawerRef } from './drawer-ref';

@Injectable({
    providedIn: 'root',
})
export class DrawerService {
    // Stack of open drawers
    private drawers: ComponentRef<DrawerComponent>[] = [];

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private appRef: ApplicationRef,
        private injector: Injector
    ) { }

    /**
     * Opens a drawer with the given component and configuration
     * @param component Component to render inside the drawer
     * @param config Configuration options for the drawer
     * @returns DrawerRef instance to control the drawer
     */
    open<T = any, D = any, R = any>(
        component: Type<T>,
        config?: DrawerConfig<D>
    ): DrawerRef<T, R> {
        // Merge with defaults
        const mergedConfig = { ...DrawerConfigDefaults.defaults, ...config, component };

        // Create drawer ref
        const drawerRef = new DrawerRef<T, R>();

        // Create drawer component
        const componentRef = this.createDrawerComponent(mergedConfig, drawerRef);

        // Add to stack
        this.drawers.push(componentRef);

        // Append to body
        this.appendDrawerToBody(componentRef);

        // Handle close
        drawerRef.afterClosed().subscribe(() => {
            this.removeDrawerFromBody(componentRef);
        });

        return drawerRef;
    }

    /**
     * Closes the currently open drawer (Top of the stack)
     */
    close(result?: any): void {
        const lastDrawer = this.drawers[this.drawers.length - 1];
        if (lastDrawer) {
            lastDrawer.instance.close(result);
        }
    }

    private createDrawerComponent(
        config: DrawerConfig,
        drawerRef: DrawerRef
    ): ComponentRef<DrawerComponent> {
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(
            DrawerComponent
        );
        const componentRef = componentFactory.create(this.injector);

        // Set config and ref
        componentRef.instance.config = config;
        componentRef.instance.drawerRef = drawerRef;

        return componentRef;
    }

    private appendDrawerToBody(componentRef: ComponentRef<DrawerComponent>): void {
        // Attach view to Angular's change detection
        this.appRef.attachView(componentRef.hostView);

        // Get DOM element
        const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
            .rootNodes[0] as HTMLElement;

        // Append to body
        document.body.appendChild(domElem);
    }

    private removeDrawerFromBody(componentRef: ComponentRef<DrawerComponent>): void {
        // Ensure we are cleaning up the correct component
        this.appRef.detachView(componentRef.hostView);
        componentRef.destroy();

        // Remove from stack
        const index = this.drawers.indexOf(componentRef);
        if (index > -1) {
            this.drawers.splice(index, 1);
        }
    }

    /**
     * Check if a drawer is currently open
     */
    isOpen(): boolean {
        return this.drawers.length > 0;
    }
}
